import { formatDateTime, formatDate } from "@/lib/format";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import type { AdminRole } from "@/hooks/useAdminAuth";
import { api } from "@/lib/api";
import { normalizeAdminRole } from "@/lib/roles";
import Pagination from "@/components/base/Pagination";
import FilterBar from "@/components/base/FilterBar";
import TableSortHeader from "@/components/base/TableSortHeader";

interface AdminRow {
  id: string;
  email: string;
  role: AdminRole;
  is_active: boolean;
  totp_verified: boolean;
  failed_otp_count: number;
  locked_until: string | null;
  last_login_at: string | null;
  created_at: string | null;
}

interface AccountApiRow {
  id: string;
  user_id?: string;
  email: string;
  username?: string | null;
  role: string;
  is_active: boolean;
  created_at?: string | null;
  last_login_at?: string | null;
}

// 角色文案与配色对齐 ADMIN_ROLES（admin / super_admin / customer_support）
const role_labels: Record<AdminRole, string> = {
  super_admin: "超级管理员",
  admin: "管理员",
  customer_support: "客服",
};

const role_colors: Record<AdminRole, string> = {
  super_admin: "bg-status-danger/10 text-status-danger",
  admin: "bg-brand/10 text-brand",
  customer_support: "bg-accent/10 text-accent",
};

const ACCOUNTS_ENDPOINT = "/quantum/business/accounts";

const backend_role: Record<AdminRole, "SUPER_ADMIN" | "OPERATOR" | "CS"> = {
  super_admin: "SUPER_ADMIN",
  admin: "OPERATOR",
  customer_support: "CS",
};

function generateInitialPassword(): string {
  const random =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `Admin#${random}`;
}

function toAdminRow(row: AccountApiRow): AdminRow {
  return {
    id: row.id,
    email: row.email,
    role: normalizeAdminRole(row.role) ?? "admin",
    is_active: row.is_active,
    totp_verified: false,
    failed_otp_count: 0,
    locked_until: null,
    last_login_at: row.last_login_at ?? null,
    created_at: row.created_at ?? null,
  };
}

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { admin: currentAdmin } = useAdminAuth();
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState<AdminRole>("admin");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [addError, setAddError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const handleSort = useCallback((field: string, dir: "asc" | "desc") => {
    setSortField(field || "created_at");
    setSortDir(dir);
    setPage(1);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(ACCOUNTS_ENDPOINT, {
        params: { page, size: pageSize },
      });
      const payload = response.data?.data ?? {};
      const rows = Array.isArray(payload.items) ? payload.items.map(toAdminRow) : [];
      const keyword = search.trim().toLowerCase();
      const filtered = keyword
        ? rows.filter((row: AdminRow) => row.email.toLowerCase().includes(keyword))
        : rows;
      const sorted = [...filtered].sort((a, b) => {
        const aVal = String(a[sortField as keyof AdminRow] ?? "");
        const bVal = String(b[sortField as keyof AdminRow] ?? "");
        return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      });
      setAdmins(sorted);
      setTotal(keyword ? filtered.length : payload.total ?? rows.length);
    } catch (err) {
      console.error("Admin users error:", err);
    } finally {
      setLoading(false);
    }
  }, [search, sortField, sortDir, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = async () => {
    setAddError("");
    if (!newEmail.trim() || !currentAdmin) return;
    if (!newPassword.trim()) {
      setAddError("请输入初始密码");
      return;
    }
    const email = newEmail.trim();
    try {
      await api.post(ACCOUNTS_ENDPOINT, {
        email,
        username: email.split("@")[0],
        password: newPassword,
        role: backend_role[newRole],
      });
      setNewEmail("");
      setNewPassword("");
      setShowNewPassword(false);
      setAddError("");
      setShowAdd(false);
      await fetchData();
    } catch (err) {
      console.error("Create admin user error:", err);
      setAddError("创建失败，请重试");
    }
  };

  const toggleStatus = async (id: string, current: boolean) => {
    try {
      await api.put(`${ACCOUNTS_ENDPOINT}/${id}`, { is_active: !current });
      await fetchData();
    } catch (err) {
      console.error("Toggle admin user status error:", err);
    }
  };

  const unlockAdmin = async (id: string) => {
    try {
      await api.put(`${ACCOUNTS_ENDPOINT}/${id}`, { is_active: true });
      await fetchData();
    } catch (err) {
      console.error("Unlock admin user error:", err);
    }
  };

  const isLocked = (a: AdminRow) => {
    if (a.locked_until && new Date(a.locked_until) > new Date()) return true;
    return false;
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-text-primary">管理员列表</h2>
        <button
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 rounded-lg bg-brand text-white text-sm font-medium hover:bg-brand-dark transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line mr-1" />
          创建管理员
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl border border-surface-border p-5">
          <h4 className="text-sm font-medium text-text-primary mb-3">创建新管理员</h4>
          {addError && (
            <p className="text-status-danger text-xs mb-3">{addError}</p>
          )}
          <div className="flex flex-wrap gap-3">
            <input
              type="email"
              placeholder="邮箱地址"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="flex-1 min-w-[200px] h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
            />
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder="初始密码"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="h-10 px-3 pr-9 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none w-44"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((v) => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors"
                tabIndex={-1}
              >
                <i className={`${showNewPassword ? "ri-eye-off-line" : "ri-eye-line"} text-base`} />
              </button>
            </div>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as AdminRole)}
              className="h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
            >
              <option value="admin">管理员</option>
              <option value="customer_support">客服</option>
              <option value="super_admin">超级管理员</option>
            </select>
            <div className="flex gap-2">
              <button
                onClick={handleAdd}
                className="px-4 py-2 rounded-lg bg-brand text-white text-sm font-medium hover:bg-brand-dark transition-colors cursor-pointer"
              >
                创建
              </button>
              <button
                onClick={() => { setShowAdd(false); setNewPassword(""); setShowNewPassword(false); setAddError(""); }}
                className="px-4 py-2 rounded-lg border border-surface-border text-text-secondary text-sm hover:bg-surface transition-colors cursor-pointer"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      <FilterBar
        search={search}
        onSearchChange={(val) => { setSearch(val); setPage(1); }}
        searchPlaceholder="搜索邮箱地址..."
        onReset={() => { setSearch(""); setSortField("created_at"); setSortDir("desc"); setPage(1); }}
      />

      <div className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-5 py-3.5 text-text-secondary font-medium text-xs">邮箱</th>
                <th className="text-left px-5 py-3.5 text-text-secondary font-medium text-xs">角色</th>
                <th className="text-left px-5 py-3.5 text-text-secondary font-medium text-xs">状态</th>
                <th className="text-left px-5 py-3.5 text-text-secondary font-medium text-xs">2FA</th>
                <th className="text-left px-5 py-3.5 text-text-secondary font-medium text-xs">登录失败</th>
                <th className="text-left px-5 py-3.5 text-text-secondary font-medium text-xs">锁定</th>
                <TableSortHeader label="最后登录" field="last_login_at" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                <TableSortHeader label="创建时间" field="created_at" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                <th className="text-right px-5 py-3.5 text-text-secondary font-medium text-xs">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-loader-4-line animate-spin text-2xl mb-2 block" />
                    加载中...
                  </td>
                </tr>
              ) : admins.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-admin-line text-3xl mb-2 block" />
                    暂无管理员
                  </td>
                </tr>
              ) : (
                admins.map((a) => (
                  <tr key={a.id} className="border-b border-surface-border hover:bg-surface/30 transition-colors">
                    <td className="px-5 py-3.5 text-text-primary">{a.email}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${role_colors[a.role]}`}>
                        {role_labels[a.role]}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      {a.is_active ? (
                        <span className="inline-flex items-center gap-1 text-xs text-status-success">
                          <i className="ri-checkbox-circle-line" /> 正常
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-text-muted">
                          <i className="ri-forbid-line" /> 已禁用
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      {a.totp_verified ? (
                        <i className="ri-shield-check-line text-status-success" />
                      ) : (
                        <i className="ri-shield-line text-text-muted" />
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-text-muted text-xs">{a.failed_otp_count}/5</td>
                    <td className="px-5 py-3.5">
                      {isLocked(a) ? (
                        <span className="inline-flex items-center gap-1 text-xs text-status-danger">
                          <i className="ri-lock-line" /> 已锁定
                        </span>
                      ) : (
                        <span className="text-text-muted text-xs">-</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-text-muted text-xs">
                      {a.last_login_at ? formatDateTime(a.last_login_at) : "从未登录"}
                    </td>
                    <td className="px-5 py-3.5 text-text-muted text-xs">{formatDate(a.created_at)}</td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/admin-users/${a.id}`)}
                          className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-surface text-text-secondary hover:text-brand transition-colors cursor-pointer"
                          title="查看详情"
                        >
                          <i className="ri-eye-line" />
                        </button>
                        <button
                          onClick={() => toggleStatus(a.id, a.is_active)}
                          className={`w-8 h-8 rounded-md flex items-center justify-center hover:bg-surface transition-colors cursor-pointer ${
                            a.is_active ? "text-status-danger hover:text-status-danger" : "text-status-success hover:text-status-success"
                          }`}
                          title={a.is_active ? "禁用" : "启用"}
                        >
                          <i className={a.is_active ? "ri-user-unfollow-line" : "ri-user-follow-line"} />
                        </button>
                        {isLocked(a) && (
                          <button
                            onClick={() => unlockAdmin(a.id)}
                            className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-surface text-status-warning hover:text-status-warning transition-colors cursor-pointer"
                            title="解锁"
                          >
                            <i className="ri-lock-unlock-line" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        )}
      </div>
    </div>
  );
}
